<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import type { RailTrain } from '@/@types/rail';
import ActionButton from '@/components/ui/ActionButton.vue';
import RoundedButton from '@/components/ui/RoundedButton.vue';
import TabsBar, { type TabItem } from '@/components/ui/TabsBar.vue';
import { formatRailArrivalTime, formatRailDepartureTime, type RailTrainTime } from '@/libs/rail-helper';
import { useGroupStore } from '@/stores/group';
import { useGtfsStore } from '@/stores/gtfs';
import { useRailStore, type RailBoardState } from '@/stores/rail';
import TrainCard from './TrainCard.vue';

const groupStore = useGroupStore();
const gtfsStore = useGtfsStore();
const railStore = useRailStore();

const router = useRouter();
const { t } = useI18n();

const props = defineProps<{
  stopId: string;
}>();

const groupId = computed<string | undefined>(() => groupStore.group?.group_id);

type BoardKey = 'departures' | 'arrivals';

interface BoardSection {
  key: BoardKey;
  titleKey: string;
  emptyKey: string;
  state: RailBoardState;
  cards: Array<{ train: RailTrain; time: RailTrainTime }>;
  retry: () => void;
}

// The station's own name once a board answered, the network's stop name meanwhile
const title = computed<string | undefined>(
  () =>
    [...railStore.departures, ...railStore.arrivals].find(train => train.station_name)?.station_name ??
    gtfsStore.gtfsData.stops[props.stopId]?.stop_name,
);

// In the API's order: by scheduled time, stable while the estimates move
const sections = computed<Array<BoardSection>>(() => [
  {
    key: 'departures',
    titleKey: 'trains.departures',
    emptyKey: 'trains.empty',
    state: railStore.departuresState,
    cards: railStore.departures.map(departure => ({
      train: departure,
      time: formatRailDepartureTime(departure),
    })),
    retry: railStore.retryDepartures,
  },
  {
    key: 'arrivals',
    titleKey: 'trains.arrivals',
    emptyKey: 'trains.emptyArrivals',
    state: railStore.arrivalsState,
    cards: railStore.arrivals.map(arrival => ({ train: arrival, time: formatRailArrivalTime(arrival) })),
    retry: railStore.retryArrivals,
  },
]);

const activeTab = ref<BoardKey>('departures');

const tabs = computed<Array<TabItem>>(() =>
  sections.value.map(section => ({ id: section.key, label: t(section.titleKey) })),
);

const activeIndex = computed<number>(() => sections.value.findIndex(({ key }) => key === activeTab.value));

// A poll failing while a board is displayed keeps that board: only an empty section shows the error
function hasError(section: BoardSection): boolean {
  return section.state === 'error' && section.cards.length === 0;
}

function isEmpty(section: BoardSection): boolean {
  return section.state === 'ready' && section.cards.length === 0;
}

watch(
  [groupId, () => props.stopId],
  ([groupId, stopId]) => {
    if (groupId) railStore.startBoardsPolling(groupId, stopId);
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  railStore.stopBoardsPolling();
});
</script>

<template>
  <div class="page">
    <div class="content train-departures-page">
      <header class="train-departures-page__header">
        <RoundedButton
          :aria-label="$t('common.back')"
          icon="arrow-left"
          size="large"
          type="outline"
          @click="router.back()"
        />
        <h1 class="train-departures-page__name">{{ title }}</h1>
      </header>

      <TabsBar v-model="activeTab" class="train-departures-page__tabs" :tabs="tabs" />

      <!-- Both boards stay mounted and slide as one track, like the schedules tabs, so each keeps its scroll -->
      <div class="train-departures-page__viewport">
        <div
          class="train-departures-page__track"
          :style="{ transform: `translateX(-${activeIndex * 100}%)` }"
        >
          <section
            v-for="section in sections"
            :key="section.key"
            class="train-departures-page__pane"
            :inert="activeTab !== section.key"
          >
            <p v-if="section.state === 'loading'" class="train-departures-page__status">
              {{ $t('common.loading') }}
            </p>

            <div v-else-if="hasError(section)" class="train-departures-page__status">
              <p class="train-departures-page__status-text">{{ $t('trains.error') }}</p>
              <ActionButton
                :label="$t('common.retry')"
                leading-icon="rotate-ccw"
                type="outline"
                @click="section.retry()"
              />
            </div>

            <p v-else-if="isEmpty(section)" class="train-departures-page__status">
              {{ $t(section.emptyKey) }}
            </p>

            <TrainCard
              v-for="card in section.cards"
              :key="`${card.train.trip_id}_${card.train.service_date}_${card.train.uic}`"
              :time="card.time"
              :train="card.train"
            />
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.train-departures-page {
  background-color: $color-white;
  color: $color-neutral-highest;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &__header {
    align-items: center;
    display: flex;
    padding: $spacing-3 $spacing-4 $spacing-1;
    position: relative;
  }

  &__name {
    font: $heading-h3;
    left: 50%;
    letter-spacing: $heading-h3-spacing;
    margin: 0;
    // Centered on the page, not on the space left over by the back button.
    max-width: 60%;
    overflow: hidden;
    position: absolute;
    text-overflow: ellipsis;
    transform: translateX(-50%);
    white-space: nowrap;
  }

  &__tabs {
    flex-shrink: 0;

    .tabs-bar__tab {
      flex: 1 1 0;
    }
  }

  &__viewport {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
  }

  &__track {
    display: flex;
    height: 100%;
    transition: transform $duration-drawer-open $easing-drawer;
    will-change: transform;
  }

  // Padded, yet exactly one track step wide
  &__pane {
    box-sizing: border-box;
    display: flex;
    flex: 0 0 100%;
    flex-direction: column;
    gap: $spacing-3;
    overflow-y: auto;
    padding: $spacing-5 $spacing-4;
  }

  &__status {
    align-items: center;
    color: $color-neutral-higher;
    display: flex;
    flex-direction: column;
    font: $text-small;
    gap: $spacing-4;
    letter-spacing: $text-small-spacing;
    margin: 0;
    padding: $spacing-5 0;
    text-align: center;
  }

  &__status-text {
    margin: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .train-departures-page__track {
    transition: none;
  }
}
</style>
